import React, { useRef, useState } from "react";
import { Scale, Upload, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const bal = (v) => `${inr(Math.abs(v))} ${v < 0 ? "CR" : "DR"}`;

const Lines = ({ title, rows, tone, testid }) => rows.length === 0 ? null : (
  <div data-testid={testid} className="space-y-1">
    <div className={`text-xs font-semibold ${tone}`}>{title} ({rows.length})</div>
    <div className="rounded-md border border-border max-h-48 overflow-auto divide-y divide-border">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-2 sm:grid-cols-[90px_130px_minmax(0,1fr)_100px_100px] gap-x-2 gap-y-0.5 px-2 py-1.5 text-xs">
          <span className="num">{r.date}</span><span className="num break-all">{r.ref || "—"}</span><span className="break-words col-span-2 sm:col-span-1">{r.particulars}</span>
          <span className="num sm:text-right">{r.debit ? `Dr ${inr(r.debit)}` : ""}</span><span className="num text-right text-[hsl(var(--positive))]">{r.credit ? `Cr ${inr(r.credit)}` : ""}</span>
        </div>
      ))}
    </div>
  </div>
);

const Diffs = ({ rows }) => rows.length === 0 ? null : (
  <div data-testid="match-amount-diff" className="space-y-1">
    <div className="text-xs font-semibold text-amber-700">Same invoice, different amount ({rows.length})</div>
    <div className="rounded-md border border-border max-h-48 overflow-auto divide-y divide-border">
      {rows.map((d, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-[130px_1fr_1fr] gap-x-2 gap-y-0.5 px-2 py-1.5 text-xs">
          <span className="num break-all">{d.mine.ref}</span>
          <span className="num">Yours: {inr(d.mine.debit || d.mine.credit)}</span>
          <span className="num">App: {inr(d.app.debit || d.app.credit)}</span>
        </div>
      ))}
    </div>
  </div>
);

const Result = ({ r }) => {
  const ok = r.difference !== null && Math.abs(r.difference) < 0.01;
  return (
    <div data-testid="match-result" className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-center">
        <div className="rounded-md border border-border px-2 py-2"><div className="text-[11px] text-muted-foreground">App closing</div><div data-testid="match-app-closing" className="num font-bold text-sm">{bal(r.app_closing)}</div></div>
        <div className="rounded-md border border-border px-2 py-2"><div className="text-[11px] text-muted-foreground">Your closing</div><div data-testid="match-my-closing" className="num font-bold text-sm">{r.my_closing === null ? "—" : bal(r.my_closing)}</div></div>
        <div className={`rounded-md border px-2 py-2 ${ok ? "border-emerald-500/40 bg-emerald-500/10" : "border-destructive/40 bg-destructive/10"}`}>
          <div className="text-[11px] text-muted-foreground">Difference</div>
          <div data-testid="match-difference" className={`num font-bold text-sm ${ok ? "text-emerald-700" : "text-destructive"}`}>{r.difference === null ? "—" : ok ? "Matched" : inr(Math.abs(r.difference))}</div>
        </div>
      </div>
      {r.difference !== null && !ok && <p className="text-xs text-muted-foreground">{r.difference > 0 ? "The app shows more due than your figure." : "The app shows less due than your figure."}{!r.sheet && " Upload your statement sheet to see which lines differ."}</p>}
      {r.sheet && <p data-testid="match-summary" className="text-xs text-muted-foreground">{r.matched} line(s) match exactly.</p>}
      <Lines testid="match-only-mine" title="In your sheet but not in the app" rows={r.only_in_mine} tone="text-destructive" />
      <Lines testid="match-only-app" title="In the app but not in your sheet" rows={r.only_in_app} tone="text-primary" />
      <Diffs rows={r.amount_diff} />
    </div>
  );
};

export const BalanceMatch = ({ partyId }) => {
  const [open, setOpen] = useState(false);
  const [amt, setAmt] = useState("");
  const [side, setSide] = useState("DR");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const ref = useRef(null);
  const form = () => {
    const fd = new FormData();
    if (amt) fd.append("closing", String((side === "CR" ? -1 : 1) * Math.abs(parseFloat(amt) || 0)));
    if (file) fd.append("file", file);
    return fd;
  };
  const run = async () => {
    if (!amt && !file) return toast.error("Enter your closing balance or choose your statement sheet");
    const fd = form();
    setBusy(true);
    try { setRes((await api.post(`/parties/${partyId}/reconcile`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const pdf = async () => {
    try {
      const res = await api.post(`/parties/${partyId}/reconcile`, form(), { params: { pdf: true }, responseType: "blob", headers: { "Content-Type": "multipart/form-data" } });
      const u = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u; a.download = "balance_match.pdf"; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Could not create the PDF"); }
  };
  const field = "h-10 px-3 rounded-md border border-input bg-card text-sm";
  return (
    <>
      <button data-testid="balance-match-btn" onClick={() => { setOpen(true); setRes(null); setFile(null); setAmt(""); }} className="h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors"><Scale size={15} /> Match balance</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="balance-match-dialog" className="bg-card max-w-3xl w-[calc(100%-1rem)] max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader><DialogTitle className="font-display text-2xl">Match with your balance</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-end min-w-0">
            <div className="min-w-0"><label className="block text-sm font-medium mb-1.5">Your closing balance</label>
              <div className="flex gap-1">
                <input data-testid="match-closing-input" type="number" className={`${field} w-32 sm:w-40 num`} value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="0.00" />
                <select data-testid="match-closing-side" className={field} value={side} onChange={(e) => setSide(e.target.value)}><option value="DR">DR (due)</option><option value="CR">CR (advance)</option></select>
              </div>
            </div>
            <div className="min-w-0 sm:flex-1 sm:min-w-[200px]"><label className="block text-sm font-medium mb-1.5">Your statement sheet (optional)</label>
              <button data-testid="match-file-btn" onClick={() => ref.current?.click()} className={`${field} w-full min-w-0 flex items-center gap-2 text-left ${file ? "text-primary border-primary" : "text-muted-foreground"}`}><Upload size={14} /><span className="truncate">{file ? file.name : "Excel / CSV with Date, Invoice no., Particulars, Debit, Credit"}</span></button>
              <input ref={ref} data-testid="match-file-input" type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }} />
              {file && <button data-testid="match-file-clear" onClick={() => setFile(null)} className="mt-1 text-xs text-muted-foreground hover:text-destructive underline">Remove file</button>}
            </div>
            <button data-testid="match-run" disabled={busy} onClick={run} className="h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">{busy && <Loader2 size={14} className="animate-spin" />} Check</button>
          </div>
          {res && <Result r={res} />}
          {res && <button data-testid="match-pdf" onClick={pdf} className="self-start h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors"><FileDown size={15} /> Download PDF</button>}
        </DialogContent>
      </Dialog>
    </>
  );
};
